import '../global.css';

import {
  IBMPlexSansArabic_400Regular,
  IBMPlexSansArabic_500Medium,
  IBMPlexSansArabic_600SemiBold,
  IBMPlexSansArabic_700Bold,
} from '@expo-google-fonts/ibm-plex-sans-arabic';
import {
  Inter_400Regular,
  Inter_500Medium,
  Inter_600SemiBold,
  Inter_700Bold,
} from '@expo-google-fonts/inter';
import {
  SpaceGrotesk_400Regular,
  SpaceGrotesk_500Medium,
  SpaceGrotesk_600SemiBold,
  SpaceGrotesk_700Bold,
} from '@expo-google-fonts/space-grotesk';
import { QueryClientProvider } from '@tanstack/react-query';
import { useFonts } from 'expo-font';
import { DarkTheme, Stack, ThemeProvider, router } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { StatusBar } from 'expo-status-bar';
import React, { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';
import { GestureHandlerRootView } from 'react-native-gesture-handler';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { Showcase } from '@/components/showcase/Showcase';
import { AppLoading } from '@/components/ui/AppLoading';
import { ErrorBoundary } from '@/components/ui/ErrorBoundary';
import { ToastProvider } from '@/components/ui/Toast';
import { AppUpdateGate } from '@/components/ui/AppUpdateGate';
import { WinCelebrationGate } from '@/components/share/WinCelebrationGate';
import { palette, ThemeProvider as AppThemeProvider } from '@/config/theme';
import { initCrashReporting } from '@/lib/analytics/firebase';
import { initAppOpenAd } from '@/lib/ads/AppOpenAds';
import { AuthProvider } from '@/lib/auth/AuthProvider';
import { listenForLeagueInviteLinks, listenForReferralLinks } from '@/lib/deeplink';
import { listenForNotificationTaps } from '@/lib/notifications';
import { initLiveNotifications } from '@/lib/notifications/live';
import { applyDirection } from '@/lib/i18n/direction';
import { initI18n, type Locale } from '@/lib/i18n';
import { LocaleProvider } from '@/lib/i18n/LocaleProvider';
import { createQueryClient } from '@/lib/query/client';

SplashScreen.preventAutoHideAsync().catch(() => {
  // Already hidden (e.g. fast refresh) - nothing to do.
});

const navigationTheme = {
  ...DarkTheme,
  colors: {
    ...DarkTheme.colors,
    primary: palette.volt,
    background: palette.gradBottom,
    card: palette.surface,
    text: palette.text,
    border: palette.line,
    notification: palette.live,
  },
};

export default function RootLayout() {
  const [fontsLoaded] = useFonts({
    Inter_400Regular,
    Inter_500Medium,
    Inter_600SemiBold,
    Inter_700Bold,
    SpaceGrotesk_400Regular,
    SpaceGrotesk_500Medium,
    SpaceGrotesk_600SemiBold,
    SpaceGrotesk_700Bold,
    IBMPlexSansArabic_400Regular,
    IBMPlexSansArabic_500Medium,
    IBMPlexSansArabic_600SemiBold,
    IBMPlexSansArabic_700Bold,
  });
  const [locale, setLocale] = useState<Locale | null>(null);
  const queryClient = useMemo(() => createQueryClient(), []);

  // Desktop web visitors see the phone-frame showcase; phones/tablets and the
  // embedded iframes get the app itself. Decided after mount to avoid a
  // hydration mismatch with the static export.
  const [showShowcase, setShowShowcase] = useState(false);
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const decide = () => {
      try {
        const embed = new URLSearchParams(window.location.search).get('embed') === '1';
        const isTop = window.self === window.top;
        setShowShowcase(isTop && !embed && window.innerWidth >= 920);
      } catch {
        setShowShowcase(false);
      }
    };
    decide();
    window.addEventListener('resize', decide);
    return () => window.removeEventListener('resize', decide);
  }, []);

  useEffect(() => {
    initI18n().then((initial) => {
      applyDirection(initial);
      setLocale(initial);
    });
  }, []);

  // Best-effort full-screen App Open ad shortly after launch (native only; the
  // web stub no-ops). Runs once per app session.
  useEffect(() => {
    // Enable Crashlytics crash collection (Android build only; no-ops elsewhere).
    void initCrashReporting();
    initAppOpenAd();
    // Register the live-score channel + background task (Android; no-ops elsewhere).
    void initLiveNotifications();
  }, []);

  const ready = fontsLoaded && locale !== null;

  // Route an inbound referral invite link (tashkeela://referral/<CODE>) to the
  // referral screen with the code pre-filled (native only; the user confirms).
  // Gated behind `ready` so a cold-start link never pushes before the expo-router
  // Stack is mounted (the black-screen race the league/notification listeners
  // below also guard against).
  useEffect(() => {
    if (!ready) return;
    return listenForReferralLinks((code) =>
      router.push({ pathname: '/referral', params: { code } } as never),
    );
  }, [ready]);

  // Route a tapped notification to its match / the Today hub (native only).
  // `ready` gates the COLD-START tap so we never push a route before the
  // expo-router Stack is mounted (that race opened the app to a black screen);
  // the effect re-runs when `ready` flips true and flushes the launching tap.
  useEffect(
    () => listenForNotificationTaps((path) => router.push(path as never), ready),
    [ready],
  );

  // Route an inbound friend-league invite link (tashkeela://league/<CODE>) to
  // the Friend Leagues screen with the code pre-filled in the join field (the
  // user confirms; we never auto-join). Gated behind `ready` for the same
  // cold-start reason as the notification listener above.
  useEffect(() => {
    if (!ready) return;
    return listenForLeagueInviteLinks((code) =>
      router.push({ pathname: '/games/friend-leagues', params: { join: code } } as never),
    );
  }, [ready]);

  useEffect(() => {
    if (ready) {
      SplashScreen.hideAsync().catch(() => {
        // Safe to ignore - splash already gone.
      });
    }
  }, [ready]);

  if (showShowcase) {
    return <Showcase />;
  }

  if (!ready || locale === null) {
    // Branded entry/loading screen (web; native shows the OS splash over this).
    return <AppLoading />;
  }

  return (
    <GestureHandlerRootView style={{ flex: 1 }}>
      <SafeAreaProvider>
        <LocaleProvider initialLocale={locale}>
          <QueryClientProvider client={queryClient}>
            <AuthProvider>
              <AppThemeProvider>
                <ThemeProvider value={navigationTheme}>
                  <ToastProvider>
                    <StatusBar style="auto" />
                    <AppUpdateGate />
                    <WinCelebrationGate />
                    <ErrorBoundary>
                      <Stack
                        screenOptions={{
                          headerShown: false,
                          // The themed root view behind the Stack supplies the
                          // canvas colour, so transitions follow the light/dark theme.
                          contentStyle: { backgroundColor: 'transparent' },
                        }}
                      />
                    </ErrorBoundary>
                  </ToastProvider>
                </ThemeProvider>
              </AppThemeProvider>
            </AuthProvider>
          </QueryClientProvider>
        </LocaleProvider>
      </SafeAreaProvider>
    </GestureHandlerRootView>
  );
}
